import { useCallback, useEffect, useMemo, useState } from 'react'
import { setLoading } from 'redux/store/common/slice'
import { TResponsiblePeople } from '../../../../ContractDetails/ContractDetailsGeneral/ResponsibleUsers/types'
import { useDispatch, useSelector } from 'react-redux'
import { useContractCategoryDetailsContext } from '../../../Providers/ContractCategoryDetailsContextProvider'
import { useContractCategoryResponsibleContext } from '../../../Providers/ContractCategoryResponsibleContextProvider'
import { TResponsibleUser } from 'features/RFP/RFPDetails/types'
import { getIsLoading } from 'redux/store/common/getters'
import { ROLES } from 'features/Permission'
import { CustomForm } from 'components/Forms/CustomForm'
import { FieldWithLabel } from 'components/FieldWithLabel/FieldWithLabel'
import { Select } from 'components/Select'
import { getAvailableResponsibleUsersRequestAsync } from '../../../api'
import { convertAvailableUsersToOptions } from 'features/RFP/RFPDetails/ResponsibleForRFP/AddResponsibleForRFP/AddResponsibleForRFP'
import { PLACEHOLDERS } from 'constants/index'

const AddResponsibleForm = ({
  selectedAnalystUuid,
  setSelectedAnalyst,
  selectedLegalUuid,
  setSelectedLegal
}: TResponsiblePeople) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(getIsLoading)

  const { state } = useContractCategoryDetailsContext()
  const responsibleContext = useContractCategoryResponsibleContext()
  const { addResponsibleUserAsync } = responsibleContext.actions
  const { details, isCommons } = state

  const [availableUsers, setAvailableUsers] = useState<TResponsibleUser[]>([])
  const handleFinishForm = useCallback(() => {
    const responsibleUsers = [selectedAnalystUuid, selectedLegalUuid].filter(
      (i) => i !== null
    ) as Array<TResponsibleUser['uuid']>

    addResponsibleUserAsync(responsibleUsers)
  }, [addResponsibleUserAsync, selectedAnalystUuid, selectedLegalUuid])
  const updateState = (dispatch) => (value) => dispatch(value)
  const getAvailableResponsibleUsers = useCallback(async () => {
    if (!details.uuid) {
      throw new Error('Contract Category ID not provided')
    }

    try {
      dispatch(setLoading(true))

      const response = await getAvailableResponsibleUsersRequestAsync(
        details.uuid
      )

      if (response?.data?.count > 0) {
        setAvailableUsers(response?.data?.results)
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [details.uuid, dispatch])

  const getSelectedAvailableUsers = useCallback(() => {
    setSelectedAnalyst(
      (isCommons
        ? details.commons_responsibles
        : details.community_responsibles
      ).find(
        (u) => u.role === (isCommons ? ROLES.COMMONS_ANALYST : ROLES.ANALYST)
      )?.uuid ?? null
    )
    setSelectedLegal(
      (isCommons
        ? details.commons_responsibles
        : details.community_responsibles
      ).find((u) => u.role === (isCommons ? ROLES.COMMONS_LEGAL : ROLES.LEGAL))
        ?.uuid ?? null
    )
  }, [details, isCommons, setSelectedAnalyst, setSelectedLegal])

  const availableAnalystUsers = useMemo(() => {
    return convertAvailableUsersToOptions(
      availableUsers,
      isCommons ? ROLES.COMMONS_ANALYST : ROLES.ANALYST
    )
  }, [availableUsers, isCommons])

  const availableLegalUsers = useMemo(() => {
    return convertAvailableUsersToOptions(
      availableUsers,
      isCommons ? ROLES.COMMONS_LEGAL : ROLES.LEGAL
    )
  }, [availableUsers, isCommons])

  useEffect(() => {
    getSelectedAvailableUsers()
  }, [getSelectedAvailableUsers])

  useEffect(() => {
    getAvailableResponsibleUsers()
  }, [getAvailableResponsibleUsers])
  return (
    <CustomForm
      id="contract-add-responsible"
      className="rfp-details-add-responsible"
      onFinish={handleFinishForm}
    >
      {!isLoading && (
        <>
          <FieldWithLabel title={`Analyst`}>
            <Select
              options={availableAnalystUsers}
              propsSelect={{
                value: selectedAnalystUuid,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: updateState(setSelectedAnalyst)
              }}
            />
          </FieldWithLabel>
          <FieldWithLabel title={`Legal`}>
            <Select
              options={availableLegalUsers}
              propsSelect={{
                value: selectedLegalUuid,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: updateState(setSelectedLegal)
              }}
            />
          </FieldWithLabel>
        </>
      )}
    </CustomForm>
  )
}

export default AddResponsibleForm
